import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { billingStatus } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [state, setState] = useState({ status: "polling", message: "Confirming your payment…" });
  const attempts = useRef(0);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!session_id) { setState({ status: "error", message: "No session id." }); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await billingStatus(session_id);
        if (r.payment_status === "paid") {
          await refresh();
          setState({ status: "paid", message: "Payment successful." });
          return;
        }
        if (r.status === "expired") {
          setState({ status: "expired", message: "Session expired." });
          return;
        }
        if (attempts.current >= 8) {
          setState({ status: "pending", message: "Still processing — please refresh in a moment." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", message: "Could not confirm payment." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [session_id, refresh]);

  const Icon = state.status === "paid" ? CheckCircle2 : state.status === "polling" ? Loader2 : XCircle;
  const color = state.status === "paid" ? "var(--brand)" : "var(--terracotta)";

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-6" data-testid="billing-success-page">
      <div className="max-w-md w-full rounded-3xl border p-10 text-center"
           style={{ background: "white", borderColor: "var(--border-default)" }}>
        <Icon className={`h-12 w-12 mx-auto ${state.status === "polling" ? "animate-spin" : ""}`} style={{ color }} />
        <p className="label-small mt-6" data-testid="billing-status">{state.status.toUpperCase()}</p>
        <h1 className="font-serif-display text-3xl mt-2" style={{ color: "var(--text-primary)" }}>
          {state.status === "paid" ? "You're all set" : state.message}
        </h1>
        {state.status === "paid" && (
          <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
            Your subscription is active. Set up your reminders from the Account page.
          </p>
        )}
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/" className="rounded-full px-6 py-3 border transition-all"
                style={{ borderColor: "var(--brand)", color: "var(--brand)" }} data-testid="back-home-btn">
            Back to SafeRxCheck
          </Link>
          {state.status === "paid" && (
            <button onClick={() => navigate("/account")}
                    className="rounded-full px-6 py-3 text-white"
                    style={{ background: "var(--brand)" }} data-testid="go-account-btn">
              Set up reminders
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
